import React, { useState, useContext, useEffect } from "react";
import { Link as RouterLink } from "react-router-dom";
import Button from "@material-ui/core/Button";
import CssBaseline from "@material-ui/core/CssBaseline";
import TextField from "@material-ui/core/TextField";
import Link from "@material-ui/core/Link";
import Grid from "@material-ui/core/Grid";
import { makeStyles } from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import ColorModeContext from "../../layout/themeContext";
import IconButton from "@material-ui/core/IconButton";
import Brightness4Icon from "@material-ui/icons/Brightness4";
import Brightness7Icon from "@material-ui/icons/Brightness7";
import { Helmet } from "react-helmet";
import bgLogin from '../../img/bgLogin.webp';
import useSettings from "../../hooks/useSettings"; // Certifique-se de que esse hook retorne uma função estável
import InputAdornment from "@material-ui/core/InputAdornment";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    height: '100vh',
    position: 'relative',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  leftSide: {
    width: '50%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      order: 1,
    },
  },
  rightSide: {
    width: '50%',
    backgroundImage: `url(${bgLogin})`,
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  paper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(4),
    borderRadius: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      boxShadow: 'none',
      padding: theme.spacing(3),
    },
  },
  logoImg: {
    width: "100%",
    maxWidth: "350px",
    height: "auto",
    maxHeight: "120px",
    margin: "0 auto",
    content: "url(" + (theme.mode === "light" ? theme.calculatedLogoLight() : theme.calculatedLogoDark()) + ")",
  },
  form: {
    width: '100%',
    marginTop: theme.spacing(1),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
    padding: theme.spacing(1.5),
  },
  iconButton: {
    position: "absolute",
    top: 10,
    right: 10,
    color: theme.palette.text.primary,
  },
  linkSignUp: {
    display: 'flex',
    gap: 2,
    alignItems: "center",
    justifyContent: "center",
  },
  errorText: {
    color: 'red',
    marginTop: theme.spacing(1),
    textAlign: 'center',
  },
  passwordField: {
    '& input[type="password"], & input[type="text"]': {
      WebkitAppearance: 'none !important',
      MozAppearance: 'none !important',
      appearance: 'none !important',
      '&::-webkit-credentials-auto-fill-button': {
        display: 'none !important',
        visibility: 'hidden !important',
      },
      '&::-webkit-contacts-auto-fill-button': {
        display: 'none !important',
        visibility: 'hidden !important',
      },
      '&::-webkit-credential-credentials-auto-fill-button': {
        display: 'none !important',
        visibility: 'hidden !important',
      },
      '&::-ms-reveal': {
        display: 'none !important',
        visibility: 'hidden !important',
      },
      '&::-ms-clear': {
        display: 'none !important',
        visibility: 'hidden !important',
      },
    },
    '& .MuiInputAdornment-root': {
      position: 'absolute',
      right: 0,
      top: '50%',
      transform: 'translateY(-50%)',
    },
  },
}));

const Login = () => {
  const classes = useStyles();
  const { colorMode } = useContext(ColorModeContext);
  const { appLogoFavicon, appName, mode } = colorMode;
  const [user, setUser] = useState({ email: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const { handleLogin } = useContext(AuthContext);
  const { getPublicSetting } = useSettings(); // Função de configuração
  const [allowSignup, setAllowSignup] = useState(false);

  // useEffect executado apenas na montagem do componente
  useEffect(() => {
    getPublicSetting("userCreation")
      .then((data) => {
        setAllowSignup(data === "enabled");
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });
  }, []); // Dependências vazias garantem que execute apenas uma vez

  const handleChangeInput = (e) => {
    setUser({ ...user, [e.target.name]: e.target.value });
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword((prevShowPassword) => !prevShowPassword);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!user.email || !user.password) {
      setLoginError("Por favor, preencha todos os campos.");
      return;
    }

    const emailPattern = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    if (!emailPattern.test(user.email)) {
      setLoginError("Formato de email inválido.");
      return;
    }

    if (user.password.length < 5) {
      setLoginError("A senha deve ter pelo menos 5 dígitos.");
      return;
    }

    try {
      await handleLogin(user);
    } catch (error) {
      setLoginError("Email ou senha incorretos.");
    }
  };

  return (
    <main className={classes.root}>
      <Helmet>
        <title>{appName || "ChatEnvio"}</title>
        <link rel="icon" href={appLogoFavicon || "/default-favicon.ico"} />
        <style>
          {`
            input[type="password"]::-webkit-credentials-auto-fill-button,
            input[type="password"]::-webkit-contacts-auto-fill-button,
            input[type="password"]::-webkit-credential-credentials-auto-fill-button,
            input[type="password"]::-ms-reveal,
            input[type="password"]::-ms-clear,
            input[type="text"]::-webkit-credentials-auto-fill-button,
            input[type="text"]::-webkit-contacts-auto-fill-button,
            input[type="text"]::-webkit-credential-credentials-auto-fill-button,
            input[type="text"]::-ms-reveal,
            input[type="text"]::-ms-clear {
              display: none !important;
              visibility: hidden !important;
              opacity: 0 !important;
              pointer-events: none !important;
            }
            input[type="password"],
            input[type="text"] {
              -webkit-appearance: none !important;
              -moz-appearance: none !important;
              appearance: none !important;
            }
          `}
        </style>
      </Helmet>
      
      <IconButton className={classes.iconButton} onClick={colorMode.toggleColorMode}>
        {mode === "dark" ? <Brightness7Icon /> : <Brightness4Icon />}
      </IconButton>
      
      <div className={classes.leftSide}>
        <Container component="main" maxWidth="xs">
          <CssBaseline />
          <div className={classes.paper}>
            <img className={classes.logoImg} alt="logo" />
            <form className={classes.form} noValidate onSubmit={handleSubmit}>
              <TextField
                variant="outlined"
                margin="normal"
                required
                fullWidth
                id="email"
                label={i18n.t("login.form.email")}
                name="email"
                value={user.email}
                onChange={handleChangeInput}
                autoComplete="email"
                autoFocus
              />
              <TextField
                variant="outlined"
                margin="normal"
                required
                fullWidth
                name="password"
                label={i18n.t("login.form.password")}
                type={showPassword ? "text" : "password"}
                id="password"
                value={user.password}
                onChange={handleChangeInput}
                autoComplete="current-password"
                className={classes.passwordField}
                inputProps={{
                  style: {
                    WebkitAppearance: 'none',
                    MozAppearance: 'none',
                    appearance: 'none'
                  }
                }}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      key="password-visibility-toggle"
                      aria-label="toggle password visibility"
                      onClick={handleTogglePasswordVisibility}
                      size="small"
                      disableRipple
                      style={{
                        padding: '8px',
                        position: 'absolute',
                        right: '14px',
                        top: '50%',
                        transform: 'translateY(-50%)'
                      }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  )
                }}
              />
              {loginError && <p className={classes.errorText}>{loginError}</p>}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                className={classes.submit}
              >
                {i18n.t("login.buttons.submit")}
              </Button>
              {allowSignup && (
                <Grid container>
                  <Grid item className={classes.linkSignUp}>
                    <span>Não tem uma conta?{' '}</span>
                    <Link component={RouterLink} to="/signup">
                      Cadastre-se!
                    </Link>
                  </Grid>
                </Grid>
              )}
            </form>
          </div>
        </Container>
      </div>
      <div className={classes.rightSide} />
    </main>
  );
};

export default Login;
